import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, Button, Input } from '../components/ui'
import { CameraCapture } from '../components/camera'
import { extractNumbers, compareNumbers, ExtractedNumber } from '../lib/numberExtractor'
import { extractIxrsSubjectId, extractIxrsProtocolId, extractVialId, extractIxrsMedId, extractIxrsMatchingIds, extractVialIdsWithFormat, ExtractedId } from '../lib/claudeVision'
import { reconcileIds, getReconciliationStatus, ReconciliationResult } from '../lib/reconcileIds'
import { DrugIdConfirmation } from '../components/verification/DrugIdConfirmation'
import { useIXVerifyAuth } from '../contexts'
import { supabase } from '../../services/supabase'
import { listSavedRecipients, invokeGenerateAndSend } from '../lib/ixverifyApi'
import { notifyVerificationComplete, notifyAdminsOfMismatch } from '../lib/notifications'
import type { SavedRecipient } from '../types'

type VerificationStep =
  | 'select-mode'         // Choose single or multi-vial
  | 'capture-ixrs'
  | 'analyzing-ixrs'
  | 'confirm-protocol'
  | 'confirm-subject'
  | 'confirm-ixrs'        // For single drug ID
  | 'confirm-drug-ids'    // For multiple drug IDs
  | 'capture-vial'        // Single vial capture
  | 'capture-vials'       // Multi-vial capture mode
  | 'analyzing-vial'
  | 'confirm-vial'        // Single vial confirm
  | 'confirm-vial-ids'    // For multiple vial IDs
  | 'result'
  | 'reconciliation'      // For multi-vial result
  | 'email'
  | 'sending'
  | 'success'

// For multi-vial capture
interface CapturedVialImage {
  id: string
  imageData: string
  extractedIds: ExtractedId[]
  status: 'pending' | 'processing' | 'complete' | 'error'
  error?: string
}

interface VerificationData {
  ixrsImage: string | null
  protocolId: string
  protocolDetectedNumbers: ExtractedNumber[]
  subjectId: string
  subjectDetectedNumbers: ExtractedNumber[]
  // Single drug ID (backwards compatible)
  ixrsNumber: string
  ixrsDetectedNumbers: ExtractedNumber[]
  ixrsOcrText: string
  // Multiple drug IDs
  ixrsDrugIds: string[]                    // Confirmed drug IDs from IXRS
  ixrsDetectedIds: ExtractedId[]           // All detected IDs before confirmation
  isMultiVial: boolean                     // Flag to track if multi-vial mode
  // Single vial (backwards compatible)
  vialImage: string | null
  vialNumber: string
  vialDetectedNumbers: ExtractedNumber[]
  vialOcrText: string
  // Multiple vials
  vialImages: CapturedVialImage[]          // Array of captured vial images
  vialIds: string[]                        // Confirmed vial IDs
  vialDetectedIds: ExtractedId[]           // All detected IDs from vials
  // Result (single vial)
  matchResult: 'match' | 'mismatch' | null
  matchSimilarity: number
  // Result (multi-vial reconciliation)
  reconciliationResult: ReconciliationResult | null
  notes: string
}

interface SendResult {
  verificationId: string
  pdfUrl: string
}

const STEPS = [
  { key: 'select-mode', label: 'Mode' },
  { key: 'capture-ixrs', label: 'IXRS Photo' },
  { key: 'confirm-protocol', label: 'Protocol' },
  { key: 'confirm-subject', label: 'Subject ID' },
  { key: 'confirm-ixrs', label: 'Drug ID(s)' },
  { key: 'capture-vial', label: 'Vial Photo(s)' },
  { key: 'confirm-vial', label: 'Vial ID(s)' },
  { key: 'result', label: 'Result' },
  { key: 'email', label: 'Send Report' },
]

// Map analyzing/sending steps to their display step
function getDisplayStep(step: VerificationStep): string {
  if (step === 'analyzing-ixrs') return 'capture-ixrs'
  if (step === 'analyzing-vial') return 'capture-vial'
  if (step === 'confirm-drug-ids') return 'confirm-ixrs'  // Multi-vial mode maps to same step
  if (step === 'capture-vials') return 'capture-vial'      // Multi-vial mode
  if (step === 'confirm-vial-ids') return 'confirm-vial'   // Multi-vial mode
  if (step === 'reconciliation') return 'result'           // Multi-vial result
  if (step === 'sending' || step === 'success') return 'email'
  return step
}

export function NewVerification() {
  const navigate = useNavigate()
  const { user, supabaseUser } = useIXVerifyAuth()
  const [step, setStep] = useState<VerificationStep>('select-mode')
  const [data, setData] = useState<VerificationData>({
    ixrsImage: null,
    protocolId: '',
    protocolDetectedNumbers: [],
    subjectId: '',
    subjectDetectedNumbers: [],
    // Single drug ID (backwards compatible)
    ixrsNumber: '',
    ixrsDetectedNumbers: [],
    ixrsOcrText: '',
    // Multiple drug IDs
    ixrsDrugIds: [],
    ixrsDetectedIds: [],
    isMultiVial: false,
    // Single vial
    vialImage: null,
    vialNumber: '',
    vialDetectedNumbers: [],
    vialOcrText: '',
    // Multiple vials
    vialImages: [],
    vialIds: [],
    vialDetectedIds: [],
    // Results
    matchResult: null,
    matchSimilarity: 0,
    reconciliationResult: null,
    notes: '',
  })
  const [emailRecipients, setEmailRecipients] = useState<string[]>([''])
  const [savedRecipients, setSavedRecipients] = useState<SavedRecipient[]>([])
  const [ocrError, setOcrError] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [sendResult, setSendResult] = useState<SendResult | null>(null)

  // Fetch saved recipients when component mounts
  useEffect(() => {
    const fetchSavedRecipients = async () => {
      if (!user) return

      try {
        // If user has a site, filter by site. If superadmin without site, get all.
        const { data, error } = await listSavedRecipients(user.site_id || undefined)

        if (error) throw error
        setSavedRecipients(data || [])
      } catch (error) {
        console.error('Error fetching saved recipients:', error)
      }
    }

    fetchSavedRecipients()
  }, [user])

  const displayStep = getDisplayStep(step)
  const currentStepIndex = STEPS.findIndex(s => s.key === displayStep)
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100

  const goToStep = (newStep: VerificationStep) => {
    setStep(newStep)
    setOcrError(null)
    setSendError(null)
  }

  const goBack = () => {
    switch (step) {
      case 'capture-ixrs':
        goToStep('select-mode')
        break
      case 'analyzing-ixrs':
      case 'confirm-protocol':
        goToStep('capture-ixrs')
        break
      case 'confirm-subject':
        goToStep('confirm-protocol')
        break
      case 'confirm-ixrs':
      case 'confirm-drug-ids':
        goToStep('confirm-subject')
        break
      case 'capture-vial':
        goToStep('confirm-ixrs')
        break
      case 'capture-vials':
        goToStep('confirm-drug-ids')
        break
      case 'analyzing-vial':
      case 'confirm-vial':
        goToStep('capture-vial')
        break
      case 'confirm-vial-ids':
        goToStep('capture-vials')
        break
      case 'result':
        goToStep('confirm-vial')
        break
      case 'reconciliation':
        goToStep('confirm-vial-ids')
        break
      case 'email':
        if (data.isMultiVial) {
          goToStep('reconciliation')
        } else {
          goToStep('result')
        }
        break
    }
  }

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel this verification?')) {
      navigate('/ixverify/dashboard')
    }
  }

  const handleSelectMode = (mode: 'single' | 'multi') => {
    setData(prev => ({ ...prev, isMultiVial: mode === 'multi' }))
    goToStep('capture-ixrs')
  }

  const handleIxrsCapture = async (imageData: string) => {
    setData(prev => ({ ...prev, ixrsImage: imageData }))
    goToStep('analyzing-ixrs')
    setOcrError(null)

    // Use the appropriate extraction based on user-selected mode
    const isMultiVialMode = data.isMultiVial

    // Extract Protocol ID, Subject ID, and first Drug ID in parallel
    // For multi-vial: We use the single-vial extraction first to get a reliable template ID
    const [protocolResult, subjectResult, firstDrugIdResult] = await Promise.all([
      extractIxrsProtocolId(imageData),
      extractIxrsSubjectId(imageData),
      extractIxrsMedId(imageData) // Always use single-vial extraction first for best accuracy
    ])

    // Process Protocol ID results
    let protocolDetectedNumbers: ExtractedNumber[] = []
    let protocolId = ''
    if (protocolResult.success && protocolResult.ids.length > 0) {
      protocolDetectedNumbers = protocolResult.ids.map(id => ({
        value: id.value,
        confidence: id.confidence,
        type: 'drug_id' as const,
        contextMatch: id.context
      }))
      protocolId = protocolDetectedNumbers.find(n => n.confidence === 'high')?.value ||
                   protocolDetectedNumbers[0]?.value || ''
    }

    // Process Subject ID results
    let subjectDetectedNumbers: ExtractedNumber[] = []
    let subjectId = ''
    if (subjectResult.success && subjectResult.ids.length > 0) {
      subjectDetectedNumbers = subjectResult.ids.map(id => ({
        value: id.value,
        confidence: id.confidence,
        type: 'drug_id' as const,
        contextMatch: id.context
      }))
      subjectId = subjectDetectedNumbers.find(n => n.confidence === 'high')?.value ||
                  subjectDetectedNumbers[0]?.value || ''
    }

    // Process Drug ID results
    let ixrsDetectedNumbers: ExtractedNumber[] = []
    let ixrsDetectedIds: ExtractedId[] = []
    let ixrsNumber = ''

    if (firstDrugIdResult.success && firstDrugIdResult.ids.length > 0) {
      // Get the first/template ID
      const firstId = firstDrugIdResult.ids[0]
      ixrsNumber = firstId.value

      if (isMultiVialMode) {
        // Multi-vial: Use the first ID as a template to find additional matching IDs
        console.log(`Multi-vial mode: Found template ID "${firstId.value}", searching for matching IDs...`)

        // Search for additional IDs matching the same format
        const matchingIdsResult = await extractIxrsMatchingIds(imageData, firstId.value)

        // Combine first ID with any additional matching IDs found
        ixrsDetectedIds = [firstId]
        if (matchingIdsResult.success && matchingIdsResult.ids.length > 0) {
          // Add additional IDs, avoiding duplicates
          for (const id of matchingIdsResult.ids) {
            if (id.value !== firstId.value) {
              ixrsDetectedIds.push(id)
            }
          }
          console.log(`Found ${ixrsDetectedIds.length} total Drug IDs matching format`)
        }
      } else {
        // Single-vial: Just use the first ID
        ixrsDetectedIds = firstDrugIdResult.ids
      }

      ixrsDetectedNumbers = ixrsDetectedIds.map(id => ({
        value: id.value,
        confidence: id.confidence,
        type: 'drug_id' as const,
        contextMatch: id.context
      }))
    } else {
      setOcrError(firstDrugIdResult.error || 'Failed to analyze IXRS document')
    }

    setData(prev => ({
      ...prev,
      protocolId,
      protocolDetectedNumbers,
      subjectId,
      subjectDetectedNumbers,
      ixrsDetectedNumbers,
      ixrsDetectedIds,
      ixrsOcrText: firstDrugIdResult.rawText || '',
      ixrsNumber
    }))
    goToStep('confirm-protocol')
  }

  const handleProtocolConfirm = () => {
    if (data.protocolId.trim()) {
      goToStep('confirm-subject')
    }
  }

  const handleSubjectConfirm = () => {
    if (data.subjectId.trim()) {
      // Route based on whether multiple drug IDs were detected
      if (data.isMultiVial) {
        goToStep('confirm-drug-ids')
      } else {
        goToStep('confirm-ixrs')
      }
    }
  }

  // Single drug ID confirmation (backwards compatible)
  const handleIxrsConfirm = () => {
    if (data.ixrsNumber.trim()) {
      goToStep('capture-vial')
    }
  }

  // Multi drug ID confirmation (checkbox list)
  const handleDrugIdsConfirm = (confirmedIds: string[]) => {
    if (confirmedIds.length === 0) return

    setData(prev => ({
      ...prev,
      ixrsDrugIds: confirmedIds,
      // Also set ixrsNumber for backwards compatibility (use first ID)
      ixrsNumber: confirmedIds[0]
    }))
    goToStep('capture-vials')
  }

  const handleVialCapture = async (imageData: string) => {
    setData(prev => ({ ...prev, vialImage: imageData }))
    goToStep('analyzing-vial')
    setOcrError(null)

    // Use Claude Vision to extract ID from vial label
    const result = await extractVialId(imageData)

    if (result.success && result.ids.length > 0) {
      // Convert Claude's ExtractedId format to our ExtractedNumber format
      const detectedNumbers: ExtractedNumber[] = result.ids.map(id => ({
        value: id.value,
        confidence: id.confidence,
        type: 'drug_id' as const,
        contextMatch: id.context
      }))

      setData(prev => ({
        ...prev,
        vialDetectedNumbers: detectedNumbers,
        vialOcrText: result.rawText || '',
        // Auto-select the first high-confidence number if available
        vialNumber: detectedNumbers.find(n => n.confidence === 'high')?.value ||
                    detectedNumbers[0]?.value || ''
      }))
      goToStep('confirm-vial')
    } else {
      setOcrError(result.error || 'Failed to analyze vial label')
      goToStep('confirm-vial')
    }
  }

  const handleVialConfirm = () => {
    if (data.vialNumber.trim()) {
      // Compare the numbers using smart comparison
      const comparison = compareNumbers(data.ixrsNumber.trim(), data.vialNumber.trim())
      setData(prev => ({
        ...prev,
        matchResult: comparison.match ? 'match' : 'mismatch',
        matchSimilarity: comparison.similarity
      }))
      goToStep('result')
    }
  }

  const handleSelectNumber = (field: 'protocolId' | 'subjectId' | 'ixrsNumber' | 'vialNumber', value: string) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  // Multi-vial capture - add a vial image to the list
  const handleMultiVialCapture = async (imageData: string) => {
    const newImage: CapturedVialImage = {
      id: crypto.randomUUID(),
      imageData,
      extractedIds: [],
      status: 'processing'
    }

    // Add to list immediately
    setData(prev => ({
      ...prev,
      vialImages: [...prev.vialImages, newImage]
    }))

    // Get the format template from the first IXRS drug ID
    const formatTemplate = data.ixrsDrugIds[0] || data.ixrsNumber

    // Process in background using format-based extraction
    try {
      // Use format-based extraction if we have a template ID
      const result = formatTemplate
        ? await extractVialIdsWithFormat(imageData, formatTemplate)
        : await extractVialId(imageData) // Fallback to single extraction

      if (result.success && result.ids.length > 0) {
        setData(prev => ({
          ...prev,
          vialImages: prev.vialImages.map(img =>
            img.id === newImage.id
              ? { ...img, extractedIds: result.ids, status: 'complete' as const }
              : img
          ),
          // Add to the list of all detected IDs (deduplicated)
          vialDetectedIds: deduplicateIds([...prev.vialDetectedIds, ...result.ids])
        }))
      } else {
        // Fall back to single vial extraction without format constraint
        console.log('Format-based extraction found no IDs, trying standard extraction')
        const singleResult = await extractVialId(imageData)
        if (singleResult.success && singleResult.ids.length > 0) {
          setData(prev => ({
            ...prev,
            vialImages: prev.vialImages.map(img =>
              img.id === newImage.id
                ? { ...img, extractedIds: singleResult.ids, status: 'complete' as const }
                : img
            ),
            vialDetectedIds: deduplicateIds([...prev.vialDetectedIds, ...singleResult.ids])
          }))
        } else {
          setData(prev => ({
            ...prev,
            vialImages: prev.vialImages.map(img =>
              img.id === newImage.id
                ? { ...img, status: 'error' as const, error: result.error || 'No IDs found' }
                : img
            )
          }))
        }
      }
    } catch (err) {
      setData(prev => ({
        ...prev,
        vialImages: prev.vialImages.map(img =>
          img.id === newImage.id
            ? { ...img, status: 'error' as const, error: err instanceof Error ? err.message : 'Unknown error' }
            : img
        )
      }))
    }
  }

  // Deduplicate extracted IDs by value
  const deduplicateIds = (ids: ExtractedId[]): ExtractedId[] => {
    const seen = new Map<string, ExtractedId>()
    for (const id of ids) {
      const existing = seen.get(id.value.toUpperCase())
      if (!existing || confidenceScore(id.confidence) > confidenceScore(existing.confidence)) {
        seen.set(id.value.toUpperCase(), id)
      }
    }
    return Array.from(seen.values())
  }

  const confidenceScore = (c: 'high' | 'medium' | 'low'): number => {
    return c === 'high' ? 3 : c === 'medium' ? 2 : 1
  }

  // User clicked "Done Capturing" in multi-vial mode
  const handleMultiVialDone = () => {
    goToStep('confirm-vial-ids')
  }

  // User confirmed the list of vial IDs
  const handleVialIdsConfirm = (confirmedIds: string[]) => {
    if (confirmedIds.length === 0) return

    // Run reconciliation
    const result = reconcileIds(data.ixrsDrugIds, confirmedIds)

    setData(prev => ({
      ...prev,
      vialIds: confirmedIds,
      // Set vialNumber for backwards compatibility
      vialNumber: confirmedIds[0],
      reconciliationResult: result,
      // Also set match result based on reconciliation
      matchResult: result.overallMatch ? 'match' : 'mismatch',
      matchSimilarity: result.overallMatch ? 1 : 0
    }))

    goToStep('reconciliation')
  }

  // Proceed to email from reconciliation result
  const handleReconciliationProceed = () => {
    goToStep('email')
  }

  const handleProceedToEmail = () => {
    goToStep('email')
  }

  const addEmailRecipient = () => {
    setEmailRecipients(prev => [...prev, ''])
  }

  const updateEmailRecipient = (index: number, value: string) => {
    setEmailRecipients(prev => {
      const updated = [...prev]
      updated[index] = value
      return updated
    })
  }

  const removeEmailRecipient = (index: number) => {
    if (emailRecipients.length > 1) {
      setEmailRecipients(prev => prev.filter((_, i) => i !== index))
    }
  }

  const handleSelectSavedRecipient = (email: string) => {
    // Add the saved recipient email if not already in the list
    if (!emailRecipients.includes(email)) {
      // Replace empty first entry or add to list
      if (emailRecipients.length === 1 && emailRecipients[0] === '') {
        setEmailRecipients([email])
      } else {
        setEmailRecipients(prev => [...prev, email])
      }
    }
  }

  const handleAddMe = () => {
    const myEmail = user?.email || supabaseUser?.email
    if (myEmail && !emailRecipients.includes(myEmail)) {
      if (emailRecipients.length === 1 && emailRecipients[0] === '') {
        setEmailRecipients([myEmail])
      } else {
        setEmailRecipients(prev => [...prev, myEmail])
      }
    }
  }

  const isMyEmailAdded = (): boolean => {
    const myEmail = user?.email || supabaseUser?.email
    return Boolean(myEmail && emailRecipients.includes(myEmail))
  }

  // Compress image to reduce payload size
  const compressImage = async (base64Image: string, maxWidth: number = 800): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        // Scale down if larger than maxWidth
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx?.drawImage(img, 0, 0, width, height)

        // Compress to JPEG with 70% quality
        resolve(canvas.toDataURL('image/jpeg', 0.7))
      }
      img.onerror = () => resolve(base64Image) // Fall back to original on error
      img.src = base64Image
    })
  }

  // Invoke function with timeout
  const invokeWithTimeout = async <T,>(
    fn: () => Promise<T>,
    timeoutMs: number = 30000
  ): Promise<T> => {
    return Promise.race([
      fn(),
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), timeoutMs)
      ),
    ])
  }

  const handleSendReport = async () => {
    const validEmails = emailRecipients.filter(e => e.trim() && e.includes('@'))
    if (validEmails.length === 0) {
      alert('Please enter at least one valid email address')
      return
    }

    goToStep('sending')
    setSendError(null)

    try {
      // Refresh the session to ensure the JWT is valid before calling the Edge Function
      const { error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) {
        console.error('Session refresh error:', refreshError)
        // Don't throw - try to proceed anyway, in case the session is still valid
      }

      const userName = user?.full_name || supabaseUser?.user_metadata?.full_name || 'Unknown User'
      const userEmail = user?.email || supabaseUser?.email || 'unknown@example.com'

      // Compress images before sending to reduce payload and processing time
      const compressedIxrs = await compressImage(data.ixrsImage || '')

      // Build request body based on single vs multi-vial mode
      let requestBody: Record<string, unknown>

      if (data.isMultiVial && data.reconciliationResult) {
        // Multi-vial mode
        const compressedVialImages = await Promise.all(
          data.vialImages.map(img => compressImage(img.imageData))
        )

        requestBody = {
          isMultiVial: true,
          ixrsImage: compressedIxrs,
          vialImages: compressedVialImages,
          protocolId: data.protocolId,
          subjectId: data.subjectId,
          ixrsDrugIds: data.ixrsDrugIds,
          vialIds: data.vialIds,
          matchedPairs: data.reconciliationResult.matchedPairs,
          unmatchedIxrsIds: data.reconciliationResult.unmatchedIxrsIds,
          extraVialIds: data.reconciliationResult.extraVialIds,
          matchResult: data.matchResult,
          recipientEmails: validEmails,
          userName,
          userEmail,
          siteId: user?.site_id || null,
          notes: data.notes || null,
          // For backwards compatibility
          ixrsNumber: data.ixrsNumber,
          vialNumber: data.vialNumber,
          matchSimilarity: data.matchSimilarity,
        }
      } else {
        // Single-vial mode (backwards compatible)
        const compressedVial = await compressImage(data.vialImage || '')

        requestBody = {
          ixrsImage: compressedIxrs,
          vialImage: compressedVial,
          protocolId: data.protocolId,
          subjectId: data.subjectId,
          ixrsNumber: data.ixrsNumber,
          vialNumber: data.vialNumber,
          matchResult: data.matchResult,
          matchSimilarity: data.matchSimilarity,
          recipientEmails: validEmails,
          userName,
          userEmail,
          siteId: user?.site_id || null,
          notes: data.notes || null,
        }
      }

      const response = await invokeWithTimeout(
        () => invokeGenerateAndSend(requestBody),
        60000 // 60 second timeout for multi-vial
      )

      if (response.error) {
        throw new Error(response.error.message || 'Failed to send report')
      }

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to send report')
      }

      setSendResult({
        verificationId: response.data.verificationId,
        pdfUrl: response.data.pdfUrl,
      })

      // Create notifications (don't wait for these)
      if (user?.id) {
        // Notify the user who performed the verification
        notifyVerificationComplete(
          user.id,
          response.data.verificationId,
          data.ixrsNumber,
          data.matchResult || 'mismatch'
        ).catch(console.error)

        // If mismatch and user has a site, notify site admins
        if (data.matchResult === 'mismatch' && user.site_id) {
          notifyAdminsOfMismatch(
            user.site_id,
            userEmail,
            response.data.verificationId,
            data.ixrsNumber,
            data.vialNumber
          ).catch(console.error)
        }
      }

      goToStep('success')
    } catch (error) {
      console.error('Send error:', error)
      setSendError(error instanceof Error ? error.message : 'Failed to send report')
      goToStep('email')
    }
  }

  const handleFinishWithoutEmail = () => {
    navigate('/ixverify/dashboard')
  }

  const handleGoToDashboard = () => {
    navigate('/ixverify/dashboard')
  }

  // Loading/Analyzing component
  const AnalyzingView = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mb-6">
        <svg className="w-10 h-10 text-primary-600 dark:text-primary-400 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400 text-center">
        {subtitle || 'Scanning for text and identifying numbers...'}
      </p>
    </div>
  )

  // Detected numbers list component
  const DetectedNumbersList = ({
    numbers,
    selectedValue,
    onSelect
  }: {
    numbers: ExtractedNumber[]
    selectedValue: string
    onSelect: (value: string) => void
  }) => {
    if (numbers.length === 0) return null

    // Separate high confidence (contextual) from others
    const highConfidence = numbers.filter(n => n.confidence === 'high')
    const otherNumbers = numbers.filter(n => n.confidence !== 'high')

    return (
      <div className="mb-4">
        {highConfidence.length > 0 && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-2">
              Best Matches (found near keywords)
            </label>
            <div className="flex flex-wrap gap-2">
              {highConfidence.slice(0, 4).map((num, index) => (
                <button
                  key={`high-${index}`}
                  onClick={() => onSelect(num.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-mono border-2 transition-colors ${
                    selectedValue === num.value
                      ? 'bg-green-100 dark:bg-green-900/40 border-green-500 dark:border-green-600 text-green-800 dark:text-green-300'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-100'
                  }`}
                >
                  {num.value}
                  {num.contextMatch && (
                    <span className="ml-2 text-xs font-sans text-green-600 dark:text-green-400">
                      ({num.contextMatch})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {otherNumbers.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {highConfidence.length > 0 ? 'Other Detected Numbers' : 'Detected Numbers (tap to select)'}
            </label>
            <div className="flex flex-wrap gap-2">
              {otherNumbers.slice(0, 6).map((num, index) => (
                <button
                  key={`other-${index}`}
                  onClick={() => onSelect(num.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-mono border transition-colors ${
                    selectedValue === num.value
                      ? 'bg-primary-100 dark:bg-primary-900/40 border-primary-500 text-primary-800 dark:text-primary-300'
                      : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {num.value}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-[calc(100vh-3rem)] flex flex-col">
      {/* Header with progress */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 sticky top-12 z-10">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Verification</h1>
            {step !== 'success' && step !== 'sending' && (
              <button
                onClick={handleCancel}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm"
              >
                Cancel
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div className="relative">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${step === 'success' ? 100 : progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              {STEPS.map((s, i) => (
                <div
                  key={s.key}
                  className={`text-xs ${
                    i <= currentStepIndex || step === 'success' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
          </div>

          {/* Step label */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {step === 'success'
              ? 'Complete!'
              : `Step ${currentStepIndex + 1} of ${STEPS.length}: ${STEPS[currentStepIndex]?.label}`}
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Step 0: Select Mode */}
          {step === 'select-mode' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Select Verification Type</h2>
                <p className="text-gray-600 dark:text-gray-400">How many vials need to be verified?</p>
              </div>

              <div className="grid gap-4">
                {/* Single Vial Option */}
                <button
                  onClick={() => handleSelectMode('single')}
                  className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 text-left hover:border-primary-500 hover:bg-primary-50 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 dark:group-hover:bg-primary-800/60 transition-colors">
                      <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Single Vial</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        Verify one drug vial against the IXRS document. Best for standard single-dose dispensing.
                      </p>
                    </div>
                    <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Multi-Vial Option */}
                <button
                  onClick={() => handleSelectMode('multi')}
                  className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 text-left hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/40 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
                      <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v4m-2-2h4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Multiple Vials</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        Verify multiple drug vials against the IXRS document. Use when dispensing multiple doses at once.
                      </p>
                    </div>
                    <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-amber-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={handleCancel}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Capture IXRS */}
          {step === 'capture-ixrs' && (
            <CameraCapture
              title="Capture IXRS Document"
              instructions="Take a clear photo of the full IXRS document page"
              onCapture={handleIxrsCapture}
              onCancel={handleCancel}
              aspectRatio="native"
            />
          )}

          {/* Analyzing IXRS */}
          {step === 'analyzing-ixrs' && (
            <AnalyzingView title="Analyzing IXRS Document" subtitle="Extracting Protocol, Subject ID and Drug ID..." />
          )}

          {/* Step 2: Confirm Protocol ID */}
          {step === 'confirm-protocol' && (
            <div className="space-y-6">
              {ocrError && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg text-sm">
                  <strong>Note:</strong> {ocrError}. Please enter the Protocol ID manually.
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  {data.ixrsImage && (
                    <div className="mb-4">
                      <img
                        src={data.ixrsImage}
                        alt="IXRS Document"
                        className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-gray-700"
                      />
                    </div>
                  )}

                  <div className="space-y-4">
                    <DetectedNumbersList
                      numbers={data.protocolDetectedNumbers}
                      selectedValue={data.protocolId}
                      onSelect={(value) => handleSelectNumber('protocolId', value)}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {data.protocolDetectedNumbers.length > 0
                          ? 'Selected/Enter the Protocol ID'
                          : 'Enter the Protocol ID'}
                      </label>
                      <Input
                        type="text"
                        value={data.protocolId}
                        onChange={(e) => setData(prev => ({ ...prev, protocolId: e.target.value }))}
                        placeholder="e.g., mRNA-1403-P301"
                        className="text-lg font-mono"
                        autoFocus={data.protocolDetectedNumbers.length === 0}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {data.protocolDetectedNumbers.length > 0
                          ? 'Tap a detected ID above or edit manually'
                          : 'Type the Protocol ID exactly as shown on the document'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Retake
                </Button>
                <Button
                  onClick={handleProtocolConfirm}
                  disabled={!data.protocolId.trim()}
                  className="flex-1"
                >
                  Continue
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm Subject ID */}
          {step === 'confirm-subject' && (
            <div className="space-y-6">
              {/* Show confirmed Protocol ID */}
              <div className="bg-primary-50 border border-primary-200 dark:bg-primary-900/20 dark:border-primary-800 rounded-lg p-3">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
              </div>

              {ocrError && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg text-sm">
                  <strong>Note:</strong> {ocrError}. Please enter the Subject ID manually.
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  {data.ixrsImage && (
                    <div className="mb-4">
                      <img
                        src={data.ixrsImage}
                        alt="IXRS Document"
                        className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-gray-700"
                      />
                    </div>
                  )}

                  <div className="space-y-4">
                    <DetectedNumbersList
                      numbers={data.subjectDetectedNumbers}
                      selectedValue={data.subjectId}
                      onSelect={(value) => handleSelectNumber('subjectId', value)}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {data.subjectDetectedNumbers.length > 0
                          ? 'Selected/Enter the Subject ID'
                          : 'Enter the Subject ID'}
                      </label>
                      <Input
                        type="text"
                        value={data.subjectId}
                        onChange={(e) => setData(prev => ({ ...prev, subjectId: e.target.value }))}
                        placeholder="e.g., US13900198"
                        className="text-lg font-mono"
                        autoFocus={data.subjectDetectedNumbers.length === 0}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {data.subjectDetectedNumbers.length > 0
                          ? 'Tap a detected number above or edit manually'
                          : 'Type the Subject ID exactly as shown on the document'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Retake
                </Button>
                <Button
                  onClick={handleSubjectConfirm}
                  disabled={!data.subjectId.trim()}
                  className="flex-1"
                >
                  Continue
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Confirm Drug ID (IXRS Number) */}
          {step === 'confirm-ixrs' && (
            <div className="space-y-6">
              {/* Show confirmed Protocol ID and Subject ID */}
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 space-y-1">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Subject ID:</strong> <span className="font-mono">{data.subjectId}</span>
                </p>
              </div>

              {ocrError && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg text-sm">
                  <strong>Note:</strong> {ocrError}. Please enter the number manually.
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  {data.ixrsImage && (
                    <div className="mb-4">
                      <img
                        src={data.ixrsImage}
                        alt="IXRS Document"
                        className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-gray-700"
                      />
                    </div>
                  )}

                  <div className="space-y-4">
                    <DetectedNumbersList
                      numbers={data.ixrsDetectedNumbers}
                      selectedValue={data.ixrsNumber}
                      onSelect={(value) => handleSelectNumber('ixrsNumber', value)}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {data.ixrsDetectedNumbers.length > 0
                          ? 'Selected/Enter the Drug ID Number from IXRS'
                          : 'Enter the Drug ID Number from IXRS'}
                      </label>
                      <Input
                        type="text"
                        value={data.ixrsNumber}
                        onChange={(e) => setData(prev => ({ ...prev, ixrsNumber: e.target.value }))}
                        placeholder="e.g., ABC-123-456"
                        className="text-lg font-mono"
                        autoFocus={data.ixrsDetectedNumbers.length === 0}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {data.ixrsDetectedNumbers.length > 0
                          ? 'Tap a detected number above or edit manually'
                          : 'Type the ID exactly as shown on the document'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Retake
                </Button>
                <Button
                  onClick={handleIxrsConfirm}
                  disabled={!data.ixrsNumber.trim()}
                  className="flex-1"
                >
                  Continue
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 4b: Confirm Multiple Drug IDs (Multi-vial mode) */}
          {step === 'confirm-drug-ids' && (
            <div className="space-y-6">
              {/* Show confirmed Protocol ID and Subject ID */}
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 space-y-1">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Subject ID:</strong> <span className="font-mono">{data.subjectId}</span>
                </p>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Multiple Drug IDs Detected:</strong> This IXRS document shows {data.ixrsDetectedIds.length} drug{data.ixrsDetectedIds.length !== 1 ? 's' : ''} to be dispensed.
                </p>
              </div>

              <DrugIdConfirmation
                title="Confirm Drug IDs from IXRS"
                subtitle="Select the drug IDs that need to be verified"
                detectedIds={data.ixrsDetectedIds}
                onConfirm={handleDrugIdsConfirm}
                onBack={goBack}
                image={data.ixrsImage}
                expectedCount={data.ixrsDetectedIds.length}
              />
            </div>
          )}

          {/* Step 5: Capture Vial (Single vial mode) */}
          {step === 'capture-vial' && (
            <div className="space-y-4">
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 mb-4 space-y-1">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Subject ID:</strong> <span className="font-mono">{data.subjectId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Drug ID:</strong> <span className="font-mono">{data.ixrsNumber}</span>
                </p>
              </div>
              <CameraCapture
                title="Capture Vial Label"
                instructions="Take a clear photo of the vial label showing the ID number"
                onCapture={handleVialCapture}
                onCancel={() => goBack()}
              />
            </div>
          )}

          {/* Step 5b: Capture Multiple Vials (Multi-vial mode) */}
          {step === 'capture-vials' && (
            <div className="space-y-4">
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 mb-4 space-y-1">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Subject ID:</strong> <span className="font-mono">{data.subjectId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Drug IDs to verify:</strong> {data.ixrsDrugIds.length}
                </p>
              </div>

              {/* Captured vials summary */}
              {data.vialImages.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Captured Vials</h3>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {data.vialDetectedIds.length} ID{data.vialDetectedIds.length !== 1 ? 's' : ''} detected
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {data.vialImages.map((img, index) => (
                        <div key={img.id} className="relative">
                          <img
                            src={img.imageData}
                            alt={`Vial ${index + 1}`}
                            className="w-full h-16 object-cover rounded"
                          />
                          <div className={`absolute bottom-0 left-0 right-0 text-center text-xs py-0.5 ${
                            img.status === 'complete' ? 'bg-green-500 text-white' :
                            img.status === 'processing' ? 'bg-yellow-500 text-white' :
                            img.status === 'error' ? 'bg-red-500 text-white' :
                            'bg-gray-500 text-white'
                          }`}>
                            {img.status === 'complete' ? `${img.extractedIds.length} ID${img.extractedIds.length !== 1 ? 's' : ''}` :
                             img.status === 'processing' ? '...' :
                             img.status === 'error' ? 'Error' : 'Pending'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <CameraCapture
                title={`Capture Vial ${data.vialImages.length + 1}`}
                instructions="Take photos of each vial label. You can capture multiple vials in one photo."
                onCapture={handleMultiVialCapture}
                onCancel={() => goBack()}
                autoRestart={true}
              />

              {data.vialImages.length > 0 && (
                <Button
                  onClick={handleMultiVialDone}
                  className="w-full"
                  disabled={data.vialImages.some(img => img.status === 'processing')}
                >
                  Done Capturing ({data.vialDetectedIds.length} ID{data.vialDetectedIds.length !== 1 ? 's' : ''} detected)
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              )}
            </div>
          )}

          {/* Analyzing Vial */}
          {step === 'analyzing-vial' && (
            <AnalyzingView title="Analyzing Vial Label" />
          )}

          {/* Step 6: Confirm Vial Number */}
          {step === 'confirm-vial' && (
            <div className="space-y-6">
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 space-y-1">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Subject ID:</strong> <span className="font-mono">{data.subjectId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Drug ID:</strong> <span className="font-mono">{data.ixrsNumber}</span>
                </p>
              </div>

              {ocrError && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg text-sm">
                  <strong>Note:</strong> {ocrError}. Please enter the number manually.
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  {data.vialImage && (
                    <div className="mb-4">
                      <img
                        src={data.vialImage}
                        alt="Vial Label"
                        className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-gray-700"
                      />
                    </div>
                  )}

                  <div className="space-y-4">
                    <DetectedNumbersList
                      numbers={data.vialDetectedNumbers}
                      selectedValue={data.vialNumber}
                      onSelect={(value) => handleSelectNumber('vialNumber', value)}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {data.vialDetectedNumbers.length > 0
                          ? 'Selected/Enter the ID Number from Vial Label'
                          : 'Enter the ID Number from Vial Label'}
                      </label>
                      <Input
                        type="text"
                        value={data.vialNumber}
                        onChange={(e) => setData(prev => ({ ...prev, vialNumber: e.target.value }))}
                        placeholder="e.g., ABC-123-456"
                        className="text-lg font-mono"
                        autoFocus={data.vialDetectedNumbers.length === 0}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {data.vialDetectedNumbers.length > 0
                          ? 'Tap a detected number above or edit manually'
                          : 'Type the ID exactly as shown on the vial'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Retake
                </Button>
                <Button
                  onClick={handleVialConfirm}
                  disabled={!data.vialNumber.trim()}
                  className="flex-1"
                >
                  Verify
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 6b: Confirm Vial IDs (Multi-vial mode) */}
          {step === 'confirm-vial-ids' && (
            <div className="space-y-6">
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 space-y-1">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Protocol:</strong> <span className="font-mono">{data.protocolId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>Subject ID:</strong> <span className="font-mono">{data.subjectId}</span>
                </p>
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  <strong>IXRS Drug IDs:</strong> {data.ixrsDrugIds.join(', ')}
                </p>
              </div>

              <DrugIdConfirmation
                title="Confirm Vial IDs"
                subtitle="Select the vial IDs that were scanned"
                detectedIds={data.vialDetectedIds}
                onConfirm={handleVialIdsConfirm}
                onBack={goBack}
                expectedCount={data.ixrsDrugIds.length}
              />
            </div>
          )}

          {/* Step 7b: Reconciliation Result (Multi-vial mode) */}
          {step === 'reconciliation' && data.reconciliationResult && (
            <div className="space-y-6">
              <Card className={`border-2 ${
                data.reconciliationResult.overallMatch
                  ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                  : 'border-red-500 bg-red-50 dark:bg-red-900/20'
              }`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    data.reconciliationResult.overallMatch ? 'bg-green-100 dark:bg-green-900/40' : 'bg-red-100 dark:bg-red-900/40'
                  }`}>
                    {data.reconciliationResult.overallMatch ? (
                      <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <h2 className={`text-2xl font-bold mb-2 ${
                    data.reconciliationResult.overallMatch ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
                  }`}>
                    {data.reconciliationResult.overallMatch ? 'ALL VERIFIED' : 'VERIFICATION ISSUE'}
                  </h2>
                  <p className={`text-sm ${
                    data.reconciliationResult.overallMatch ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    {getReconciliationStatus(data.reconciliationResult).summary}
                  </p>
                </CardContent>
              </Card>

              {/* Matched Pairs */}
              {data.reconciliationResult.matchedPairs.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Verified ({data.reconciliationResult.matchedPairs.length})
                    </h3>
                    <div className="space-y-2">
                      {data.reconciliationResult.matchedPairs.map((pair, index) => (
                        <div key={index} className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                          <div>
                            <span className="font-mono text-sm">IXRS: {pair.ixrsId}</span>
                            <span className="mx-2 text-gray-400 dark:text-gray-500">=</span>
                            <span className="font-mono text-sm">Vial: {pair.vialId}</span>
                          </div>
                          <span className="text-green-600 dark:text-green-400 text-sm">{Math.round(pair.similarity * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Unmatched IXRS IDs (Missing Vials) */}
              {data.reconciliationResult.unmatchedIxrsIds.length > 0 && (
                <Card className="border-red-200 dark:border-red-800">
                  <CardContent className="p-4">
                    <h3 className="font-medium text-red-800 dark:text-red-300 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Missing Vials ({data.reconciliationResult.unmatchedIxrsIds.length})
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-400 mb-2">These IXRS drug IDs were not found on any vial:</p>
                    <div className="space-y-1">
                      {data.reconciliationResult.unmatchedIxrsIds.map((id, index) => (
                        <div key={index} className="font-mono text-sm bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">
                          {id}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Extra Vial IDs */}
              {data.reconciliationResult.extraVialIds.length > 0 && (
                <Card className="border-orange-200 dark:border-orange-800">
                  <CardContent className="p-4">
                    <h3 className="font-medium text-orange-800 dark:text-orange-300 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Extra Vials ({data.reconciliationResult.extraVialIds.length})
                    </h3>
                    <p className="text-sm text-orange-700 dark:text-orange-400 mb-2">These vial IDs were not found on the IXRS:</p>
                    <div className="space-y-1">
                      {data.reconciliationResult.extraVialIds.map((id, index) => (
                        <div key={index} className="font-mono text-sm bg-orange-50 dark:bg-orange-900/20 rounded px-3 py-2">
                          {id}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              <Card>
                <CardContent className="p-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={data.notes}
                    onChange={(e) => setData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add any notes about this verification..."
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    rows={3}
                  />
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
                <Button onClick={handleReconciliationProceed} className="flex-1">
                  Continue to Email
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 7: Result (Single vial mode) */}
          {step === 'result' && (
            <div className="space-y-6">
              <Card className={`border-2 ${
                data.matchResult === 'match'
                  ? 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                  : 'border-red-500 bg-red-50 dark:bg-red-900/20'
              }`}>
                <CardContent className="p-8 text-center">
                  {data.matchResult === 'match' ? (
                    <>
                      <div className="w-24 h-24 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-16 h-16 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold text-green-800 dark:text-green-300 mb-2">MATCH</h2>
                      <p className="text-green-700 dark:text-green-400">
                        The Drug ID and Vial ID match successfully.
                      </p>
                      {data.matchSimilarity < 1 && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                          Similarity: {Math.round(data.matchSimilarity * 100)}%
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="w-24 h-24 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-16 h-16 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold text-red-800 dark:text-red-300 mb-2">MISMATCH</h2>
                      <p className="text-red-700 dark:text-red-400">
                        The Drug ID and Vial ID do not match.
                      </p>
                      {data.matchSimilarity > 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                          Similarity: {Math.round(data.matchSimilarity * 100)}%
                        </p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Verification Details</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Protocol:</span>
                      <span className="font-mono font-medium">{data.protocolId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Subject ID:</span>
                      <span className="font-mono font-medium">{data.subjectId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Drug ID (IXRS):</span>
                      <span className="font-mono font-medium">{data.ixrsNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Vial ID:</span>
                      <span className="font-mono font-medium">{data.vialNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Result:</span>
                      <span className={`font-medium ${
                        data.matchResult === 'match' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {data.matchResult === 'match' ? 'Match' : 'Mismatch'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Thumbnails */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">IXRS Document</p>
                  {data.ixrsImage && (
                    <img
                      src={data.ixrsImage}
                      alt="IXRS"
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Vial Label</p>
                  {data.vialImage && (
                    <img
                      src={data.vialImage}
                      alt="Vial"
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
                <Button onClick={handleProceedToEmail} className="flex-1">
                  Send Report
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </Button>
              </div>
            </div>
          )}

          {/* Step 6: Email */}
          {step === 'email' && (
            <div className="space-y-6">
              {sendError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  <strong>Error:</strong> {sendError}
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Send Verification Report</h3>

                  {/* Quick Add Buttons */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quick Add
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {/* Me Button */}
                      <button
                        onClick={handleAddMe}
                        disabled={isMyEmailAdded()}
                        className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                          isMyEmailAdded()
                            ? 'bg-primary-100 dark:bg-primary-900/40 border-primary-500 text-primary-800 dark:text-primary-300 cursor-default'
                            : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 hover:border-primary-300 dark:hover:border-primary-700'
                        }`}
                      >
                        {isMyEmailAdded() ? (
                          <>
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Me
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Me
                          </>
                        )}
                      </button>

                      {/* Saved Recipients */}
                      {savedRecipients.map((recipient) => {
                        const isSelected = emailRecipients.includes(recipient.email)
                        return (
                          <button
                            key={recipient.id}
                            onClick={() => handleSelectSavedRecipient(recipient.email)}
                            disabled={isSelected}
                            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                              isSelected
                                ? 'bg-primary-100 dark:bg-primary-900/40 border-primary-500 text-primary-800 dark:text-primary-300 cursor-default'
                                : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            {isSelected && (
                              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {recipient.label || recipient.email}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Recipients
                    </label>
                    {emailRecipients.map((email, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => updateEmailRecipient(index, e.target.value)}
                          placeholder="recipient@example.com"
                          className="flex-1"
                        />
                        {emailRecipients.length > 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeEmailRecipient(index)}
                            className="px-3"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </Button>
                        )}
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addEmailRecipient}
                      className="w-full"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Another Recipient
                    </Button>
                  </div>

                  {/* Notes field */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes (optional)
                    </label>
                    <textarea
                      value={data.notes}
                      onChange={(e) => setData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Add any notes about this verification..."
                      className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 resize-none focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card className={`border ${
                data.matchResult === 'match' ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {data.matchResult === 'match' ? (
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    )}
                    <div>
                      <p className={`font-medium ${
                        data.matchResult === 'match' ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
                      }`}>
                        Verification {data.matchResult === 'match' ? 'Matched' : 'Mismatched'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {data.protocolId} | Subject: {data.subjectId} | Drug: {data.ixrsNumber} | Vial: {data.vialNumber}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={handleSendReport}
                  className="flex-1"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Report
                </Button>
              </div>

              <button
                onClick={handleFinishWithoutEmail}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-2"
              >
                Skip and finish without sending
              </button>
            </div>
          )}

          {/* Sending state */}
          {step === 'sending' && (
            <div className="space-y-6">
              <AnalyzingView
                title="Generating Report..."
                subtitle="Creating PDF and sending email. This may take a moment..."
              />
              <div className="text-center">
                <button
                  onClick={() => goToStep('email')}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                >
                  Cancel and go back
                </button>
              </div>
            </div>
          )}

          {/* Success state */}
          {step === 'success' && sendResult && (
            <div className="space-y-6">
              <Card className="border-2 border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/20">
                <CardContent className="p-8 text-center">
                  <div className="w-24 h-24 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-16 h-16 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-green-800 dark:text-green-300 mb-2">Report Sent!</h2>
                  <p className="text-green-700 dark:text-green-400 mb-4">
                    Your verification report has been sent successfully.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                    ID: {sendResult.verificationId}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">What's Next?</h3>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Email sent to {emailRecipients.filter(e => e.includes('@')).length} recipient(s)
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      PDF report saved for your records
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Verification logged in system
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {sendResult.pdfUrl && (
                <a
                  href={sendResult.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View PDF Report
                  </Button>
                </a>
              )}

              <Button onClick={handleGoToDashboard} className="w-full">
                Return to Dashboard
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
